import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { useComponentLoadingStateStore } from "../../store/useComponentLoadingStateStore";
import { useEffect, useState } from "react";
import { ErrorBox } from "../GenericLayout/ErrorBox";
import { SuccessBox } from "../GenericLayout/SuccessBox";
import { ROOMS_DATA } from "../../configs/ROOMS_DATA";
import ReCAPTCHA from "react-google-recaptcha";
import { t } from "i18next";
import { ConfirmButton } from "../MaterialCustomComponents/NewsletterComponents";
import { SimpleBox } from "../MaterialCustomComponents";
interface EmailData {
  fullName: string;
  email: string;
  phone: string;
  text: string;
}
const initialFormData = {
  fullName: "",
  email: "",
  phone: "",
  text: "",
};
const BoothContactForm = ({ boothID }: { boothID: string }) => {
  const hall = boothID?.split("-")?.[0];
  const Exhibitor = ROOMS_DATA?.[hall]?.filter(
    (stand) => stand.name === boothID
  )[0];
  const { isBoothContactFormOpen, setIsBoothContactFormOpen } =
    useComponentLoadingStateStore();

  const [formData, setFormData] = useState<EmailData>(initialFormData);
  const [fetching, setFetching] = useState<boolean>(false);
  const [complete, setComplete] = useState<boolean>(false);
  const [captchaValidated, setCaptchaValidated] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string>("");

  const onInputData = (prop: string, value: string) => {
    setFormData({ ...formData, [prop]: value });
  };
  const initializeStates = () => {
    setFetching(false);
    setCaptchaValidated(false);
  };
  useEffect(() => {
    initializeStates();
    setErrorMsg("");
    setComplete(false);
    return initializeStates;
  }, []);

  const sendEmail = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFetching(true);
    setComplete(false);
    setErrorMsg("");
    const contacts = Exhibitor.data.email;
    // this is where your mailchimp request is made
    const res = await fetch("/api/sendEmail", {
      body: JSON.stringify({
        ...formData,
        contacts: process.env.NEXT_PUBLIC_EMAIL_CONTACTS
          ? process.env.NEXT_PUBLIC_EMAIL_CONTACTS
          : contacts,
      }),

      headers: {
        "Content-Type": "application/json",
      },

      method: "POST",
    });
    setFetching(false);
    try {
      const resHasError = (await res.json()).error;
      if (resHasError) {
        return setErrorMsg(resHasError);
      }
      setFormData(initialFormData);
      return setComplete(true);
    } catch (e) {
      console.error(e);
    }
  };
  function onChange(value: null | string) {
    setCaptchaValidated(!!value);
  }
  const cancelForm = () => {
    setIsBoothContactFormOpen(false);
    setFormData(initialFormData);
    initializeStates();
  };

  return (
    <SimpleBox sx={{ pointerEvents: "auto", position: "absolute" }}>
      <Dialog
        open={isBoothContactFormOpen}
        onClose={() => {
          setIsBoothContactFormOpen(false);
          initializeStates();
        }}
      >
        <form onSubmit={sendEmail}>
          <DialogTitle>{t("contactForm.title")}</DialogTitle>
          <DialogContent>
            <DialogContentText>{t("contactForm.subtitle")}</DialogContentText>
            <TextField
              required={true}
              autoFocus
              margin="dense"
              id="name"
              label="Full Name"
              type="text"
              fullWidth
              variant="standard"
              onInput={(e: React.ChangeEvent<HTMLInputElement>) =>
                onInputData("fullName", e.target.value)
              }
              value={formData.fullName}
              sx={{
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "black",
                },
                "& .MuiInputBase-root.MuiInput-underline:after": {
                  border: "none",
                },
              }}
            />
            <TextField
              required={true}
              autoFocus
              margin="dense"
              id="email"
              value={formData.email}
              onInput={(e: React.ChangeEvent<HTMLInputElement>) =>
                onInputData("email", e.target.value)
              }
              label="Email Address"
              type="email"
              fullWidth
              variant="standard"
              sx={{
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "black",
                },
                "& .MuiInputBase-root.MuiInput-underline:after": {
                  border: "none",
                },
              }}
            />
            <TextField
              autoFocus
              margin="dense"
              id="phone"
              onInput={(e: React.ChangeEvent<HTMLInputElement>) =>
                onInputData("phone", e.target.value)
              }
              value={formData.phone}
              label="Phone Number"
              type="number"
              fullWidth
              variant="standard"
              sx={{
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "black",
                },
                "& .MuiInputBase-root.MuiInput-underline:after": {
                  border: "none",
                },
              }}
            />
            <TextField
              required={true}
              autoFocus
              margin="dense"
              id="message"
              label="Message"
              onInput={(e: React.ChangeEvent<HTMLInputElement>) =>
                onInputData("text", e.target.value)
              }
              value={formData.text}
              type="text"
              fullWidth
              multiline
              rows={4}
              variant="standard"
              sx={{
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "black",
                },
                "& .MuiInputBase-root.MuiInput-underline:after": {
                  border: "none",
                },
              }}
            />
          </DialogContent>
          <DialogActions sx={{ display: "flex", justifyContent: "center" }}>
            <ReCAPTCHA
              sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_KEY || ""}
              onChange={onChange}
              size="normal"
            />
          </DialogActions>
          <DialogActions sx={{ display: "flex", justifyContent: "center" }}>
            <ConfirmButton variant="primary" onClick={cancelForm}>
              {t("contactForm.close")}
            </ConfirmButton>
            <ConfirmButton
              variant="primary"
              type="submit"
              disabled={fetching || !captchaValidated}
            >
              {t("contactForm.send")}
            </ConfirmButton>
          </DialogActions>
          <DialogActions sx={{ display: "flex", justifyContent: "center" }}>
            {errorMsg.length > 0 && (
              <ErrorBox msg={t("contactForm.error_message")} />
            )}
            {complete && <SuccessBox msg={t("contactForm.success_message")} />}
          </DialogActions>
        </form>
      </Dialog>
    </SimpleBox>
  );
};

export default BoothContactForm;
